import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { notificationsApi } from '@/api/notifications';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { Notification } from '@/types/types';

export interface NotificationsContextValue {
    state: { notifications: Notification[] };
    actions: {
        addFCMNotification: (
            messageId: string,
            jobId: string,
            serviceId: string,
            title: string,
            timestamp: number,
        ) => void;
        markAsRead: (notification: Notification) => Promise<void>;
        refreshNotifications: () => Promise<void>;
    };
}

const NotificationsContext = createContext<NotificationsContextValue>({
    state: { notifications: [] },
    actions: {
        addFCMNotification: () => {},
        markAsRead: async () => {},
        refreshNotifications: async () => {},
    },
});

const renameKey = <T extends Record<string, unknown>>(
    obj: T,
    from: string,
    to: string,
): T => {
    if (!(from in obj)) return obj;
    const { [from]: value, ...rest } = obj;
    return { ...rest, [to]: value } as unknown as T;
};

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;

    useEffect(() => {
        if (!loggedIn) setNotifications([]);
    }, [loggedIn]);

    const refreshNotifications = useCallback(async () => {
        try {
            const res = await notificationsApi.list();
            const raw = res.data?.data ?? [];
            const normalized = raw.map((n) =>
                renameKey(n as unknown as Record<string, unknown>, '_id', 'id'),
            ) as unknown as Notification[];
            setNotifications(normalized);
        } catch {}
    }, []);

    const addFCMNotification = useCallback(
        (
            messageId: string,
            jobId: string,
            serviceId: string,
            title: string,
            timestamp: number,
        ) => {
            setNotifications((prev) => [
                {
                    id: messageId,
                    messageId,
                    job_id: jobId,
                    service_id: serviceId,
                    title,
                    timestamp,
                    read: false,
                },
                ...prev,
            ]);
        },
        [],
    );

    const markAsRead = useCallback(async (notification: Notification) => {
        try {
            if (notification.messageId) {
                await notificationsApi.markAsRead(notification.messageId);
            } else if (notification.id) {
                await notificationsApi.markAsRead(notification.id);
            }
            setNotifications((prev) =>
                prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
            );
        } catch {}
    }, []);

    return (
        <NotificationsContext.Provider
            value={{
                state: { notifications },
                actions: { addFCMNotification, markAsRead, refreshNotifications },
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationsContext);
